import { cn } from '@/lib/utils';

/**
 * MarkdownRenderer - Renders markdown-style text with formatting preserved
 * 
 * Supports:
 * - Line breaks (\n\n for paragraphs, \n for line breaks)
 * - **bold** text
 * - *italic* text
 * - ### Headings
 * - - Bullet lists
 * - 1. Numbered lists
 * - --- Horizontal dividers
 * - Emojis and special characters
 */
const MarkdownRenderer = ({ content, className, truncate = false, maxLines = 2 }) => {
  if (!content) return null;

  // Parse markdown-style content into React elements
  const parseContent = (text) => {
    // Split into blocks by double newlines (paragraphs) or single newlines
    const blocks = text.split(/\n\n+/);
    
    return blocks.map((block, blockIndex) => {
      // Check for horizontal divider
      if (block.trim() === '---' || block.trim() === '***') {
        return <hr key={blockIndex} className="my-3 border-border" />;
      }

      // Check for heading (### )
      if (block.trim().startsWith('### ')) {
        const headingText = block.trim().substring(4);
        return (
          <h3 key={blockIndex} className="font-semibold text-foreground mt-3 mb-1 first:mt-0">
            {parseInline(headingText)}
          </h3>
        );
      }

      // Check for heading (## )
      if (block.trim().startsWith('## ')) {
        const headingText = block.trim().substring(3);
        return (
          <h2 key={blockIndex} className="font-bold text-foreground text-lg mt-4 mb-2 first:mt-0">
            {parseInline(headingText)}
          </h2>
        );
      }

      // Split block by single newlines for line breaks within a paragraph
      const lines = block.split('\n');
      
      // Check if this is a list
      const isBulletList = lines.every(line => 
        line.trim() === '' || line.trim().startsWith('- ') || line.trim().startsWith('• ')
      );
      const isNumberedList = lines.every(line => 
        line.trim() === '' || /^\d+\.\s/.test(line.trim())
      );

      if (isBulletList && lines.some(l => l.trim().startsWith('- ') || l.trim().startsWith('• '))) {
        return (
          <ul key={blockIndex} className="list-disc list-inside space-y-1 my-2">
            {lines.filter(line => line.trim()).map((line, lineIndex) => {
              const content = line.trim().replace(/^[-•]\s*/, '');
              return (
                <li key={lineIndex} className="text-muted-foreground">
                  {parseInline(content)}
                </li>
              );
            })}
          </ul>
        );
      }

      if (isNumberedList && lines.some(l => /^\d+\.\s/.test(l.trim()))) {
        return (
          <ol key={blockIndex} className="list-decimal list-inside space-y-1 my-2">
            {lines.filter(line => line.trim()).map((line, lineIndex) => {
              const content = line.trim().replace(/^\d+\.\s*/, '');
              return (
                <li key={lineIndex} className="text-muted-foreground">
                  {parseInline(content)}
                </li>
              );
            })}
          </ol>
        );
      }

      // Regular paragraph with line breaks
      return (
        <p key={blockIndex} className="mb-2 last:mb-0">
          {lines.map((line, lineIndex) => (
            <span key={lineIndex}>
              {parseInline(line)}
              {lineIndex < lines.length - 1 && <br />}
            </span>
          ))}
        </p>
      );
    });
  };

  // Parse inline formatting (bold, italic)
  const parseInline = (text) => {
    if (!text) return null;

    const parts = [];
    let remaining = text;
    let keyIndex = 0;

    // Pattern for **bold**, *italic*, or plain text
    const regex = /(\*\*[^*]+\*\*|\*[^*]+\*)/g;
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(remaining)) !== null) {
      // Add text before the match
      if (match.index > lastIndex) {
        parts.push(
          <span key={keyIndex++}>
            {remaining.substring(lastIndex, match.index)}
          </span>
        );
      }

      const matchText = match[0];
      
      // Bold (**text**)
      if (matchText.startsWith('**') && matchText.endsWith('**')) {
        parts.push(
          <strong key={keyIndex++} className="font-semibold text-foreground">
            {matchText.slice(2, -2)}
          </strong>
        );
      }
      // Italic (*text*)
      else if (matchText.startsWith('*') && matchText.endsWith('*')) {
        parts.push(
          <em key={keyIndex++} className="italic">
            {matchText.slice(1, -1)}
          </em>
        );
      }

      lastIndex = regex.lastIndex;
    }

    // Add remaining text
    if (lastIndex < remaining.length) {
      parts.push(
        <span key={keyIndex++}>
          {remaining.substring(lastIndex)}
        </span>
      );
    }

    return parts.length > 0 ? parts : text;
  };

  // For truncated view, just show plain text with ellipsis
  if (truncate) {
    const plainText = content
      .replace(/\*\*/g, '')
      .replace(/\*/g, '')
      .replace(/###?\s*/g, '')
      .replace(/---/g, '')
      .replace(/\n+/g, ' ')
      .trim();

    return (
      <p className={cn(
        "text-muted-foreground",
        `line-clamp-${maxLines}`,
        className
      )}>
        {plainText}
      </p>
    );
  }

  return (
    <div className={cn("text-sm text-muted-foreground space-y-0", className)}>
      {parseContent(content)}
    </div>
  );
};

export { MarkdownRenderer };
